import React from 'react';
import { MapContainer, Marker, Polyline, Popup, TileLayer } from 'react-leaflet';
import { MapPin, Route as RouteIcon, Ruler, Search, Spline, X } from 'lucide-react';
import { toast } from 'react-toastify';
import Button from '../ui/Button';
import { cn } from '../../lib/cn';
import MapClickHandler from '../map/MapClickHandler';
import MapFlyController from '../map/MapFlyController';
import ConfirmStopMoveDialog from './ConfirmStopMoveDialog';
import { useAuth } from '../../hooks/useAuth';
import {
  DEFAULT_CENTER,
  formatDistance,
  formatDuration,
  highlightMatch,
  routePaletteColor,
  routeSegmentDistances
} from '../../utils/routeManagementHelpers';
import {
  distanceLabelIcon,
  editingIcon,
  pendingIcon,
  routeStopIcon,
  savedIcon
} from '../../utils/routeManagementIcons';
import { getRoutePath } from '../../utils/api';

// Renders a small distance pill at the midpoint of each route segment.
function RouteDistanceLabels({ segments }) {
  return segments.map((seg, index) => (
    <Marker
      key={`seg-${index}`}
      position={seg.mid}
      icon={distanceLabelIcon(formatDistance(seg.meters))}
      interactive={false}
      keyboard={false}
    />
  ));
}

const GLASS = 'bg-white/95 backdrop-blur-md border border-slate-200 shadow-pop rounded-xl';

// A stop is only drawable when it has real coordinates. Missing/zero coords come
// from route stops that reference a stop master that no longer resolves.
function hasValidCoords(stop) {
  return (
    Number.isFinite(stop?.lat) &&
    Number.isFinite(stop?.lng) &&
    !(stop.lat === 0 && stop.lng === 0)
  );
}

export default function MapArea({ controller }) {
  const {
    activeTab,
    cancelRouteDraggedPosition,
    editingStop,
    editName,
    editPendingMarker,
    flyTarget,
    handleMapClick,
    handleRouteStopDragEnd,
    handleStopDragEnd,
    handleViewingRouteDragEnd,
    isDraggingMarker,
    mapRouteStops,
    mapSearchFocused,
    mapSearchRef,
    mapStopSearch,
    mapStopSearchResults,
    pendingMarker,
    routeColorMap,
    routeDragPending,
    saveRouteDraggedPosition,
    saving,
    selectedRouteList,
    setFlyTarget,
    setMapSearchFocused,
    setMapStopSearch,
    setVisibleStopIds,
    startEdit,
    stops,
    stopsOnMap,
    viewingRoute
  } = controller;

  // Every selected route except the focused one — drawn as thinner background
  // lines. The focused route (viewingRoute) gets bold styling and markers.
  const otherRoutes = React.useMemo(
    () => (selectedRouteList || []).filter((route) => route._id !== viewingRoute?._id),
    [selectedRouteList, viewingRoute]
  );

  const focusedColor =
    (viewingRoute && routeColorMap?.get(viewingRoute._id)) || routePaletteColor(0);

  const { isEditor } = useAuth();
  const inStopWorkspace = activeTab === 'stops';
  const inMapPage = activeTab === 'map';
  const inRoutesPage = activeTab === 'routes';
  const showStopLayers = inStopWorkspace || inMapPage;

  // Stops whose stopId doesn't resolve to a master can carry missing/zero
  // coordinates; drawing them would fling a straight line off to (0,0).
  const focusedStops = React.useMemo(
    () => (mapRouteStops || []).filter(hasValidCoords),
    [mapRouteStops]
  );
  const focusedPositions = focusedStops.map((stop) => [stop.lat, stop.lng]);

  // Per-segment + total straight-line distances for the primary route.
  const { segments, total } = React.useMemo(
    () =>
      routeSegmentDistances(
        focusedStops.map((stop) => ({ lat: stop.lat, lng: stop.lng }))
      ),
    [focusedStops]
  );

  // The primary route polyline is shown when viewing a route (stop/map layers)
  // or anywhere on the routes page with ≥2 stops.
  const showPrimaryRoute =
    focusedPositions.length > 1 && ((showStopLayers && viewingRoute) || inRoutesPage);

  /*
   * Road-following path for the route being viewed.
   *
   * The road path is the PRIMARY view: opening a route fetches it and draws it, so
   * what is on screen is the way the bus actually travels. Straight-line is the
   * alternative, shown only when the operator asks for it — and the two are never
   * drawn together, so there is no ambiguity about which line is which.
   *
   * Straight-line is still what appears while the road path is loading, and if the
   * routing service cannot answer at all; both cases are labelled as such rather
   * than left to look like a road measurement.
   */
  const [roadInfo, setRoadInfo] = React.useState(null);
  const [roadLoading, setRoadLoading] = React.useState(false);
  const [roadFailed, setRoadFailed] = React.useState(false);
  // The operator's explicit choice to see straight-line instead of the road path.
  const [preferStraightLine, setPreferStraightLine] = React.useState(false);

  const viewingRouteId = viewingRoute?._id;

  // Load the road path whenever a different route is opened.
  React.useEffect(() => {
    setRoadInfo(null);
    setRoadFailed(false);
    setPreferStraightLine(false);

    if (!viewingRouteId) return undefined;

    let cancelled = false;
    setRoadLoading(true);

    getRoutePath(viewingRouteId)
      .then(({ data }) => {
        if (cancelled) return;
        if (data.source !== 'ors') {
          // No usable road answer — fall back to straight-line and say why.
          setRoadFailed(true);
          toast.info(data.fallbackReason || 'Road network unavailable — showing straight-line');
          return;
        }
        setRoadInfo({
          distanceMeters: data.distanceMeters,
          durationSeconds: data.durationSeconds,
          sampled: data.sampled,
          viaPointCount: data.viaPointCount,
          stopCount: data.stopCount,
          // The service returns [lng, lat]; Leaflet wants [lat, lng].
          geometry: (data.geometry || []).map(([lng, lat]) => [lat, lng])
        });
      })
      .catch(() => {
        if (cancelled) return;
        setRoadFailed(true);
        toast.error('Could not load the road path — showing straight-line');
      })
      .finally(() => {
        if (!cancelled) setRoadLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [viewingRouteId]);

  // Road is shown unless it is unavailable or the operator switched it off.
  const roadActive = !!roadInfo && !preferStraightLine;
  const canToggleRoad = !!roadInfo;
  const displayTotal = roadActive ? roadInfo.distanceMeters : total;
  const displayDuration = roadActive ? formatDuration(roadInfo.durationSeconds) : null;

  return (
    <div className="absolute inset-0">
      {showStopLayers && stops.length > 0 && (
        <div
          className="absolute top-3 left-1/2 -translate-x-1/2 z-[1001] w-[min(360px,calc(100%-1.5rem))]"
          ref={mapSearchRef}
        >
          <div className={cn(GLASS, 'flex items-center pl-3 pr-1.5')}>
            <Search className="h-3.5 w-3.5 text-slate-400 shrink-0" />
            <input
              type="text"
              className="flex-1 bg-transparent border-0 outline-none px-2 py-2 text-[13px] placeholder:text-slate-400"
              placeholder="Find a stop already in the catalog"
              value={mapStopSearch}
              onChange={(event) => {
                setMapStopSearch(event.target.value);
                setMapSearchFocused(true);
              }}
              onFocus={() => setMapSearchFocused(true)}
            />
            {mapStopSearch && (
              <button
                type="button"
                onClick={() => {
                  setMapStopSearch('');
                  setMapSearchFocused(false);
                }}
                className="h-6 w-6 inline-flex items-center justify-center rounded text-slate-400 hover:bg-slate-100 hover:text-slate-700"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>

          {mapSearchFocused && mapStopSearchResults.length > 0 && (
            <div className={cn(GLASS, 'mt-2 max-h-64 overflow-y-auto animate-slide-up')}>
              {mapStopSearchResults.map((stop) => {
                const stopIndex = stops.indexOf(stop);
                return (
                  <button
                    key={stop._id}
                    type="button"
                    onMouseDown={() => {
                      setFlyTarget({ lat: stop.lat, lng: stop.lng });
                      setVisibleStopIds((ids) => new Set([...ids, stop._id]));
                      setMapStopSearch('');
                      setMapSearchFocused(false);
                    }}
                    className="w-full flex items-center justify-between gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-brand-50/60 transition-colors"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="h-5 w-5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-semibold flex items-center justify-center shrink-0 tabular-nums">
                        {stopIndex + 1}
                      </span>
                      <span className="text-[13px] font-medium text-slate-900 truncate text-left">
                        {highlightMatch(stop.stopName, mapStopSearch)}
                      </span>
                    </div>
                    <span className="font-mono text-[10.5px] text-slate-400 shrink-0">
                      {stop.lat.toFixed(4)}, {stop.lng.toFixed(4)}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Centred confirmation for a stop moved from inside a route. */}
      {inRoutesPage && (
        <ConfirmStopMoveDialog
          pending={routeDragPending}
          routeLabel={
            viewingRoute
              ? `Route ${viewingRoute.routeNumber} ${viewingRoute.direction}`
              : ''
          }
          saving={saving}
          onCancel={cancelRouteDraggedPosition}
          onConfirm={saveRouteDraggedPosition}
        />
      )}

      {showPrimaryRoute && (
        <div className={cn(GLASS, 'absolute top-3 left-3 z-[1001] px-3.5 py-2.5 min-w-[180px] animate-slide-up')}>
          <div className="flex items-center gap-1.5 text-slate-500">
            <RouteIcon className="h-3.5 w-3.5" />
            <span className="text-[10.5px] font-semibold uppercase tracking-wider">Route length</span>
            <span
              className={cn(
                'ml-auto inline-flex items-center gap-1 text-[9.5px] font-semibold px-1.5 py-0.5 rounded-full border',
                roadActive
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
                  : 'bg-slate-50 border-slate-200 text-slate-500'
              )}
            >
              {roadActive ? <RouteIcon className="h-2.5 w-2.5" /> : <Spline className="h-2.5 w-2.5" />}
              {roadActive ? 'road' : 'straight-line'}
            </span>
          </div>
          <p className="mt-1 text-[22px] font-semibold text-slate-900 tabular-nums leading-none">
            {roadLoading ? '—' : formatDistance(displayTotal)}
          </p>
          <p className="mt-1 text-[11.5px] text-slate-500">
            {focusedStops.length} stops · {segments.length} segment{segments.length === 1 ? '' : 's'}
            {displayDuration ? ` · ~${displayDuration}` : ''}
          </p>

          {/* Why the straight-line figure is on screen, whenever it is not the
              operator's own choice. */}
          {viewingRoute?._id && !roadActive && (
            <p className="mt-1 text-[10.5px] leading-snug text-slate-400">
              {roadLoading
                ? 'Loading the road path…'
                : roadFailed
                  ? 'Road path unavailable — straight-line estimate.'
                  : 'Straight-line, by your choice.'}
            </p>
          )}

          {viewingRoute?._id && canToggleRoad && (
            <button
              type="button"
              onClick={() => setPreferStraightLine((previous) => !previous)}
              className="mt-2 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-slate-200 bg-white text-[11.5px] font-medium text-slate-600 hover:border-brand-300 hover:text-brand-700 transition-colors"
            >
              <Ruler className="h-3 w-3" />
              {roadActive ? 'Show straight-line' : 'Show road path'}
            </button>
          )}
        </div>
      )}

      <MapContainer
        center={DEFAULT_CENTER}
        zoom={13}
        style={{ height: '100%', width: '100%' }}
      >
        <TileLayer
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
        />
        <MapClickHandler onMapClick={handleMapClick} />
        <MapFlyController flyTarget={flyTarget} />

        {showStopLayers && (
          <>
            {inStopWorkspace && pendingMarker && !editingStop && (
              <Marker position={[pendingMarker.lat, pendingMarker.lng]} icon={pendingIcon()}>
                <Popup>
                  <strong>{controller.stopName || 'New stop'}</strong>
                  <br />
                  {pendingMarker.lat.toFixed(6)}, {pendingMarker.lng.toFixed(6)}
                </Popup>
              </Marker>
            )}

            {stopsOnMap.map((stop) => {
              const stopIndex = stops.indexOf(stop);
              const isEditing = editingStop?._id === stop._id;
              const position =
                isEditing && editPendingMarker
                  ? [editPendingMarker.lat, editPendingMarker.lng]
                  : [stop.lat, stop.lng];

              return (
                <Marker
                  key={stop._id}
                  position={position}
                  icon={isEditing ? editingIcon(stopIndex + 1) : savedIcon(stopIndex + 1)}
                  draggable={isEditor && inStopWorkspace}
                  eventHandlers={{
                    click: () => {
                      if (isEditor && inStopWorkspace && !isEditing && !isDraggingMarker.current) {
                        startEdit(stop);
                      }
                    },
                    dragstart: () => {
                      if (!inStopWorkspace) {
                        return;
                      }

                      isDraggingMarker.current = true;
                    },
                    dragend: (event) => {
                      if (!inStopWorkspace) {
                        return;
                      }

                      setTimeout(() => {
                        isDraggingMarker.current = false;
                      }, 150);
                      const { lat, lng } = event.target.getLatLng();
                      handleStopDragEnd(stop._id, lat, lng);
                    }
                  }}
                >
                  <Popup>
                    <strong>
                      #{stopIndex + 1} {isEditing ? editName || stop.stopName : stop.stopName}
                    </strong>
                    <br />
                    {position[0].toFixed(6)}, {position[1].toFixed(6)}
                    {isEditing && (
                      <>
                        <br />
                        <em style={{ color: '#7c3aed', fontSize: '11px' }}>
                          Drag to move. Save to keep.
                        </em>
                      </>
                    )}
                  </Popup>
                </Marker>
              );
            })}

            {otherRoutes.map((route) => {
              const positions = [...route.stops]
                .sort((left, right) => left.sequence - right.sequence)
                .filter(hasValidCoords)
                .map((stop) => [stop.lat, stop.lng]);
              if (positions.length < 2) return null;
              return (
                <Polyline
                  key={`mp-extra-${route._id}`}
                  positions={positions}
                  color={routeColorMap?.get(route._id)}
                  weight={3}
                  opacity={0.45}
                  eventHandlers={{ click: () => controller.focusRoute(route) }}
                />
              );
            })}

            {viewingRoute && focusedPositions.length > 1 && (
              <>
                {focusedStops.map((stop) => (
                  <Marker
                    key={`${stop.stopId}-${stop.sequence}`}
                    position={[stop.lat, stop.lng]}
                    icon={routeStopIcon(stop.sequence)}
                  >
                    <Popup>
                      <strong>
                        #{stop.sequence} {stop.stopName}
                      </strong>
                      <br />
                      {stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}
                    </Popup>
                  </Marker>
                ))}
                {/* One line at a time: the road path when it is available, the
                    straight-line otherwise. Drawing both at once made it unclear
                    which one the reported distance referred to. */}
                {roadActive ? (
                  <Polyline
                    positions={roadInfo.geometry}
                    color={focusedColor}
                    weight={5}
                    opacity={0.95}
                  />
                ) : (
                  <>
                    <Polyline
                      positions={focusedPositions}
                      color={focusedColor}
                      weight={5}
                      opacity={0.95}
                      dashArray="10 6"
                    />
                    {/* Per-leg labels are straight-line measurements, so they
                        belong only to the straight-line view. */}
                    <RouteDistanceLabels segments={segments} />
                  </>
                )}
              </>
            )}
          </>
        )}

        {inRoutesPage && (
          <>
            {otherRoutes.map((route) => {
              const positions = [...route.stops]
                .sort((left, right) => left.sequence - right.sequence)
                .filter(hasValidCoords)
                .map((stop) => [stop.lat, stop.lng]);
              if (positions.length < 2) return null;
              return (
                <Polyline
                  key={`extra-${route._id}`}
                  positions={positions}
                  color={routeColorMap?.get(route._id)}
                  weight={3}
                  opacity={0.45}
                  eventHandlers={{ click: () => controller.focusRoute(route) }}
                />
              );
            })}

            {mapRouteStops.map((stop, index) => {
              if (!hasValidCoords(stop)) return null;
              return (
                <Marker
                  key={`${stop.stopId}-${stop.sequence}`}
                  position={[stop.lat, stop.lng]}
                  icon={routeStopIcon(stop.sequence)}
                  draggable={isEditor}
                  eventHandlers={{
                    dragend: (event) => {
                      const { lat, lng } = event.target.getLatLng();
                      if (viewingRoute) {
                        handleViewingRouteDragEnd(stop.sequence, lat, lng);
                      } else {
                        handleRouteStopDragEnd(index, lat, lng);
                      }
                    }
                  }}
                >
                  <Popup>
                    <strong>
                      #{stop.sequence} {stop.stopName}
                    </strong>
                    <br />
                    {stop.lat.toFixed(6)}, {stop.lng.toFixed(6)}
                    <br />
                    <em style={{ fontSize: '11px', color: '#64748b' }}>
                      Drag to reposition
                    </em>
                  </Popup>
                </Marker>
              );
            })}

            {focusedPositions.length > 1 && (
              <>
                {/* One line at a time: the road path when it is available, the
                    straight-line otherwise. Drawing both at once made it unclear
                    which one the reported distance referred to. */}
                {roadActive ? (
                  <Polyline
                    positions={roadInfo.geometry}
                    color={focusedColor}
                    weight={5}
                    opacity={0.95}
                  />
                ) : (
                  <>
                    <Polyline
                      positions={focusedPositions}
                      color={focusedColor}
                      weight={5}
                      opacity={0.95}
                      dashArray="10 6"
                    />
                    {/* Per-leg labels are straight-line measurements, so they
                        belong only to the straight-line view. */}
                    <RouteDistanceLabels segments={segments} />
                  </>
                )}
              </>
            )}
          </>
        )}
      </MapContainer>
    </div>
  );
}
